import React, { useState } from 'react';
import { Search, Heart, User, HelpCircle, Menu, X, Bell, Loader2 } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAvatarFaceUrl } from '../utils/getAvatarFaceUrl';
import { supabase } from '../supabaseClient';
import SearchBox from './SearchBox';
import { buildOffersUrl, suggestionToParams } from '../lib/searchNav';

const NotificationBell = ({ user, navigate }) => {
    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(true);

    React.useEffect(() => {
        const fetchCount = async () => {
            try {
                const { data, error } = await supabase.rpc('get_new_notifications_count', { p_user_id: user.id });
                if (!error && typeof data === 'number') setCount(data);
            } catch (e) {
                console.error("Notif error", e);
            } finally {
                setLoading(false);
            }
        };
        fetchCount();
    }, [user.id]);

    const handleClick = async () => {
        setCount(0);
        navigate('/offres_vie?source=notifications');

        await supabase
            .from('profiles')
            .update({ last_notification_check: new Date().toISOString() })
            .eq('id', user.id);
    };

    if (loading) return <div className="h-9 w-9 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>;

    return (
        <button
            onClick={handleClick}
            data-tour="tour-nav-notifications"
            className="flex flex-col items-center cursor-pointer group gap-1 relative"
        >
            <div className="h-9 w-9 flex items-center justify-center relative">
                <Bell className={`w-7 h-7 transition-colors ${count > 0 ? 'text-blue-600 fill-blue-100' : 'text-slate-900 group-hover:text-blue-600'}`} />
                {count > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-sm border border-white animate-in zoom-in">
                        {count > 99 ? '99+' : count}
                    </span>
                )}
            </div>
            <span className={`text-[12px] font-bold ${count > 0 ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-600'} transition-colors relative group-hover:[box-shadow:inset_0_-6px_0_0_#bae6fd]`}>
                {count > 0 ? 'Nouveautés' : 'Notifs'}
            </span>
        </button>
    );
};

const Header = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();

    const goToOffers = (params) => {
        navigate(buildOffersUrl(params));
        setMobileSearchOpen(false);
    };

    // Ferme l'overlay mobile à chaque navigation
    React.useEffect(() => {
        setMobileSearchOpen(false);
    }, [location]);

    return (
        <>
            <header className="fixed top-0 left-0 right-0 h-[56px] md:h-[72px] bg-white border-b border-slate-200 z-50 px-4 md:px-6">

                {/* ========== MOBILE HEADER ========== */}
                <div className="flex md:hidden items-center justify-between h-full">
                    {/* Left: Logo */}
                    <Link to="/" className="flex items-center">
                        <span className="text-lg font-black text-slate-900 tracking-tighter">
                            Trouve<span className="text-blue-500">tonVIE</span>
                        </span>
                    </Link>

                    {/* Right: Icons */}
                    <div className="flex items-center gap-1">
                        {/* Search Icon */}
                        <button
                            onClick={() => setMobileSearchOpen(true)}
                            className="p-2.5 text-slate-700 hover:bg-slate-100 rounded-full transition-colors"
                            aria-label="Rechercher"
                        >
                            <Search size={20} />
                        </button>

                        {/* Favorites with Blue Dot */}
                        <Link
                            to="/mes-favoris"
                            data-tour="tour-nav-favoris-mobile"
                            className="p-2.5 text-slate-700 hover:bg-slate-100 rounded-full transition-colors relative"
                        >
                            <Heart size={20} />
                            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-blue-500 rounded-full" />
                        </Link>

                        {/* Hamburger Menu */}
                        <button
                            onClick={() => setMobileMenuOpen(true)}
                            className="p-2.5 text-slate-700 hover:bg-slate-100 rounded-full transition-colors"
                            aria-label="Menu"
                        >
                            <Menu size={20} />
                        </button>
                    </div>
                </div>

                {/* ========== DESKTOP HEADER ========== */}
                <div className="hidden md:flex max-w-7xl mx-auto h-full items-center justify-between gap-8">

                    {/* Logo & Navigation */}
                    <div className="flex items-center gap-10">
                        <Link to="/" className="flex items-center gap-2 group">
                            <span className="text-xl font-black text-slate-900 tracking-tighter group-hover:scale-105 transition-transform">
                                Trouve<span className="text-blue-500">tonVIE</span>
                            </span>
                        </Link>

                        <nav className="flex items-center gap-6 text-sm font-bold text-slate-900">
                            <Link to="/offres_vie" data-tour="tour-nav-offres" className="relative hover:text-slate-900 transition-all hover:[box-shadow:inset_0_-8px_0_0_#bae6fd]">Offres</Link>
                            <Link to="/offres-plus" data-tour="tour-nav-offresplus" className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent font-black hover:opacity-80 transition-opacity">Offres+</Link>
                            <Link to="/les-entreprises" data-tour="tour-nav-entreprises" className="relative hover:text-slate-900 transition-all hover:[box-shadow:inset_0_-8px_0_0_#bae6fd]">Entreprises</Link>
                        </nav>
                    </div>

                    {/* Search & Actions */}
                    <div className="flex items-center gap-0">
                        {/* Search Bar */}
                        <SearchBox
                            wrapperClassName="hidden lg:block w-[300px] mr-20"
                            inputClassName="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-full text-sm font-medium text-slate-700 outline-none focus:ring-2 focus:ring-blue-600/10 focus:border-blue-600 transition-all placeholder:text-slate-400"
                            placeholder="Rechercher un VIE"
                            onSubmit={(term) => goToOffers({ search: term })}
                            onPick={(s) => goToOffers(suggestionToParams(s))}
                        />

                        <div className="flex items-center gap-10 h-8">
                            {/* Trouve+ Image */}
                            <Link to="/trouve-plus" className="flex items-center gap-1 group">
                                <img src="/assets/Trouve_plus.png" alt="Trouve+" className="h-10 w-auto object-contain" />
                            </Link>

                            {/* Notifications (Premium) */}
                            {user && (
                                <NotificationBell user={user} navigate={navigate} />
                            )}

                            {/* FAQ Link */}
                            <Link to="/faq" className="flex flex-col items-center cursor-pointer group gap-1">
                                <div className="h-9 w-9 flex items-center justify-center">
                                    <HelpCircle className="w-7 h-7 text-slate-900 group-hover:text-blue-600 transition-colors" />
                                </div>
                                <span className="text-[12px] font-bold text-slate-400 group-hover:text-slate-600 relative transition-all group-hover:[box-shadow:inset_0_-6px_0_0_#bae6fd]">FAQ</span>
                            </Link>

                            {/* Favorites */}
                            <Link to="/mes-favoris" data-tour="tour-nav-favoris" className="flex flex-col items-center cursor-pointer group gap-1">
                                <div className="h-9 w-9 flex items-center justify-center">
                                    <Heart className="w-7 h-7 text-slate-900 group-hover:text-blue-600 group-hover:fill-blue-600 transition-colors" />
                                </div>
                                <span className="text-[12px] font-bold text-slate-400 group-hover:text-slate-600">Mes favoris</span>
                            </Link>

                            {/* Profile OR Login */}
                            {user ? (
                                <Link to="/profil" data-tour="tour-nav-profil" className="flex flex-col items-center cursor-pointer group gap-1">
                                    <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center text-white font-black text-sm border border-slate-200 group-hover:border-blue-600 transition-colors shadow-sm">
                                        {(user?.user_metadata?.first_name?.[0] || user?.email?.[0] || '?').toUpperCase()}
                                    </div>
                                    <span className="text-[12px] font-bold text-slate-400 group-hover:text-slate-600 relative transition-all group-hover:[box-shadow:inset_0_-6px_0_0_#bae6fd]">Mon profil</span>
                                </Link>
                            ) : (
                                <Link to="/login" className="flex items-center gap-2 px-6 py-2.5 bg-slate-900 text-white rounded-full text-xs font-bold hover:bg-blue-600 transition-all shadow-md active:scale-95">
                                    <User size={16} />
                                    Se connecter
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* ========== MOBILE SEARCH OVERLAY ========== */}
            {mobileSearchOpen && (
                <div className="fixed inset-0 bg-white z-[80] md:hidden">
                    <div className="flex items-center gap-3 p-4 border-b border-slate-200">
                        <button
                            onClick={() => setMobileSearchOpen(false)}
                            className="p-2 -ml-2 text-slate-500 hover:text-slate-900"
                        >
                            <X size={24} />
                        </button>
                        <SearchBox
                            wrapperClassName="flex-1"
                            inputClassName="w-full py-2 text-base text-slate-900 placeholder-slate-400 outline-none bg-transparent"
                            showIcon={false}
                            placeholder="Rechercher un VIE, une entreprise..."
                            autoFocus
                            onSubmit={(term) => goToOffers({ search: term })}
                            onPick={(s) => goToOffers(suggestionToParams(s))}
                        />
                    </div>
                </div>
            )}

            {/* ========== MOBILE DRAWER MENU ========== */}
            {/* Backdrop */}
            <div
                className={`fixed inset-0 bg-black/50 z-[60] transition-opacity duration-300 md:hidden ${mobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                onClick={() => setMobileMenuOpen(false)}
            />

            {/* Drawer - slides from RIGHT */}
            <div className={`fixed top-0 right-0 h-full w-[280px] bg-white z-[70] transform transition-transform duration-300 ease-out md:hidden ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                {/* Drawer Header */}
                <div className="flex items-center justify-between p-4 border-b border-slate-200">
                    <span className="font-bold text-slate-900">Menu</span>
                    <button
                        onClick={() => setMobileMenuOpen(false)}
                        className="p-2 -mr-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                        aria-label="Fermer le menu"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Drawer Navigation */}
                <nav className="p-4 space-y-1">
                    <Link
                        to="/trouve-plus"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 text-slate-900 font-semibold rounded-xl hover:bg-sky-50 transition-colors"
                    >
                        <img src="/assets/Trouve_plus.png" alt="Trouve+" className="h-6 w-auto" />
                    </Link>

                    <div className="border-t border-slate-200 my-4" />

                    <Link
                        to="/faq"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 text-slate-600 font-medium rounded-xl hover:bg-sky-50 transition-colors"
                    >
                        <HelpCircle size={20} className="text-slate-400" />
                        FAQ
                    </Link>
                </nav>

                {/* Drawer Footer */}
                <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 bg-slate-50">
                    {user ? (
                        <Link
                            to="/profil"
                            onClick={() => setMobileMenuOpen(false)}
                            className="flex items-center gap-3 px-4 py-3 text-slate-900 font-semibold rounded-xl hover:bg-white transition-colors"
                        >
                            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-black text-sm border border-slate-200">
                                {(user?.user_metadata?.first_name?.[0] || user?.email?.[0] || '?').toUpperCase()}
                            </div>
                            Mon profil
                        </Link>
                    ) : (
                        <Link
                            to="/login"
                            onClick={() => setMobileMenuOpen(false)}
                            className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-slate-900 text-white rounded-full text-sm font-bold hover:bg-blue-600 transition-all"
                        >
                            <User size={18} />
                            Se connecter
                        </Link>
                    )}
                </div>
            </div>
        </>
    );
};

export default Header;
